import { ChildProfile } from '@yoyoclub/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { childProfileApi } from '../../services/profileApi';
import Button from '../ui/Button';
import Card from '../ui/Card';

export default function ChildProfileFormModal({
  profile,
  onClose,
}: {
  profile: ChildProfile | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(['profiles']);
  const [formData, setFormData] = useState({
    name: profile?.name || '',
    preferredName: profile?.preferredName || '',
    birthYear: profile?.birthDate ? new Date(profile.birthDate).getFullYear().toString() : '',
    birthMonth: profile?.birthDate ? (new Date(profile.birthDate).getMonth() + 1).toString() : '',
    notes: profile?.notes || '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const birthDate = new Date(
        parseInt(formData.birthYear),
        parseInt(formData.birthMonth) - 1,
        1
      );

      const profileData = {
        name: formData.name,
        preferredName: formData.preferredName,
        birthDate: birthDate.toISOString(),
        notes: formData.notes || undefined,
      };

      if (profile) {
        await childProfileApi.updateProfile(profile.id, profileData);
      } else {
        await childProfileApi.createProfile(profileData);
      }

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save child profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {profile ? t('profiles:child.editTitle') : t('profiles:child.addTitle')}
          </h2>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profiles:child.name')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('profiles:child.namePlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">{t('profiles:child.nameHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profiles:child.preferredName')} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.preferredName}
                onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('profiles:child.preferredNamePlaceholder')}
              />
              <p className="mt-1 text-xs text-gray-500">{t('profiles:child.preferredNameHint')}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profiles:child.birthday')} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-4">
                <select
                  required
                  value={formData.birthYear}
                  onChange={(e) => setFormData({ ...formData, birthYear: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">{t('profiles:child.selectYear')}</option>
                  {Array.from({ length: 18 }, (_, i) => new Date().getFullYear() - i).map(
                    (year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    )
                  )}
                </select>
                <select
                  required
                  value={formData.birthMonth}
                  onChange={(e) => setFormData({ ...formData, birthMonth: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">{t('profiles:child.selectMonth')}</option>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => {
                    const monthKey = new Date(2000, month - 1)
                      .toLocaleDateString('en-US', { month: 'long' })
                      .toLowerCase() as keyof typeof t;
                    return (
                      <option key={month} value={month}>
                        {t(`profiles:child.months.${monthKey}`)}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('profiles:child.specialNeeds')}
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder={t('profiles:child.specialNeedsPlaceholder')}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" variant="primary" disabled={loading} className="flex-1">
                {loading ? t('profiles:child.saving') : t('profiles:child.save')}
              </Button>
              <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
                {t('profiles:child.cancel')}
              </Button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
}
